import React from "react";
import * as KotOR from "@/apps/forge/KotOR";
import { TabState, UpdateFileOptions } from "@/apps/forge/states/tabs/TabState";

/**
 * Sanitizes a string to be a valid ResRef (max 16 chars, lowercase, alphanumeric + underscore only)
 */
export const sanitizeResRef = (value: string): string => {
  return value.substring(0, 16).toLowerCase().replace(/[^a-z0-9_]/g, '');
};

/**
 * Clamps a number to valid BYTE range (0-255)
 */
export const clampByte = (value: number): number => {
  return Math.max(0, Math.min(255, value));
};

/**
 * Clamps a number to valid WORD range (1-65535)
 */
export const clampWord = (value: number): number => {
  return Math.max(1, Math.min(0xFFFF, value || 1));
};

/** Mark the UTx tab dirty so the tab-strip dirty-dot updates. */
export function markUtxTabDirty(tab: TabState): void {
  if (tab.file) {
    tab.file.unsaved_changes = true;
  }
  tab.editorFileUpdated();
}

/**
 * Snapshot the last-exported blueprint before a live UTx field is written.
 * Save/export passes skipHistory so this does not push undo or re-dirt the tab.
 */
export function beginUtxFileUpdate(tab: TabState, options?: UpdateFileOptions): void {
  if (options?.skipHistory) {
    return;
  }
  if (options?.coalesceKey) {
    tab.captureCoalescedUndo(options.coalesceKey);
  } else {
    tab.captureUndoSnapshot();
  }
  markUtxTabDirty(tab);
}

/** Mark the tab dirty and remount the form after restoring a UTx snapshot. */
export function finishUtxUndoApply(tab: TabState, attachPreview?: () => Promise<void>): void {
  markUtxTabDirty(tab);
  const fireLoad = () => tab.processEventListener("onEditorFileLoad", [tab]);
  if (attachPreview) {
    void Promise.resolve(attachPreview()).then(fireLoad, fireLoad);
  } else {
    fireLoad();
  }
}

/**
 * Creates a handler for updating number fields on a tab state
 */
export const createNumberFieldHandler = <T extends TabState>(
  setter: (value: number) => void,
  property: keyof T,
  tab: T,
  parser: (value: number) => number = (v) => v
) => {
  return (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const raw = parseInt(e.target.value) || 0;
    const value = parser(raw);
    setter(value);
    tab.setProperty(property as keyof TabState, value);
    tab.updateFile({ coalesceKey: String(property) });
  };
};

export const createNumberArrayFieldHandler = <T extends TabState>(
  setter: (value: number[]) => void,
  index: number,
  property: keyof T,
  tab: T
) => {
  return (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const raw = parseInt(e.target.value) || 0;
    const value = tab.getProperty(property) as number[];
    value[index] = raw;
    setter([...value]);
    tab.setProperty(property as keyof TabState, value);
    tab.updateFile({ coalesceKey: `${String(property)}:${index}` });
  };
};


/**
 * Creates a handler for updating BYTE fields (0-255)
 */
export const createByteFieldHandler = <T extends TabState>(
  setter: (value: number) => void,
  property: keyof T,
  tab: T
) => {
  return createNumberFieldHandler(setter, property, tab, clampByte);
};

/**
 * Creates a handler for updating WORD fields (1-65535)
 */
export const createWordFieldHandler = <T extends TabState>(
  setter: (value: number) => void,
  property: keyof T,
  tab: T
) => {
  return createNumberFieldHandler(setter, property, tab, clampWord);
};

/**
 * Creates a handler for updating boolean/checkbox fields
 */
export const createBooleanFieldHandler = <T extends TabState>(
  setter: (value: boolean) => void,
  property: keyof T,
  tab: T
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    setter(value);
    tab.setProperty(property as keyof TabState, value);
    tab.updateFile({ coalesceKey: String(property) });
  };
};

/**
 * Creates a handler for updating boolean/checkbox fields
 */
export const createForgeCheckboxFieldHandler = <T extends TabState>(
  setter: (value: boolean) => void,
  property: keyof T,
  tab: T
) => {
  return (value: boolean) => {
    setter(value);
    tab.setProperty(property as keyof TabState, value);
    tab.updateFile({ coalesceKey: String(property) });
  };
};

/**
 * Creates a handler for updating ResRef string fields
 */
export const createResRefFieldHandler = <T extends TabState>(
  setter: (value: string) => void,
  property: keyof T,
  tab: T
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = sanitizeResRef(e.target.value);
    setter(value);
    tab.setProperty(property as keyof TabState, value);
    tab.updateFile({ coalesceKey: String(property) });
  };
};

/**
 * Creates a handler for updating CExoString (textarea) fields
 */
export const createCExoStringFieldHandler = <T extends TabState>(
  setter: (value: string) => void,
  property: keyof T,
  tab: T
) => {
  return (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setter(e.target.value);
    tab.setProperty(property as keyof TabState, e.target.value);
    tab.updateFile({ coalesceKey: String(property) });
  };
};

/**
 * Creates a handler for updating CExoLocString fields
 */
export const createCExoLocStringFieldHandler = <T extends TabState>(
  setter: (value: KotOR.CExoLocString) => void,
  property: keyof T,
  tab: T
) => {
  return (value: KotOR.CExoLocString) => {
    setter(value);
    tab.setProperty(property as keyof TabState, value);
    tab.updateFile({ coalesceKey: String(property) });
  };
};

